import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  ChatMessage,
  ChatResponse,
  ConversationRecord,
  ConversationsResponse,
  MCPServerStatusData,
  ProviderStatusData,
  StreamEvent,
  ToolsResponse,
} from '../types';

/**
 * @public
 */
export interface McpChatApi {
  sendChatMessage(
    messages: ChatMessage[],
    enabledTools?: string[],
    signal?: AbortSignal,
    conversationId?: string,
  ): Promise<ChatResponse>;
  sendChatMessageStream(
    messages: ChatMessage[],
    enabledTools: string[],
    onEvent: (event: StreamEvent) => void,
    signal?: AbortSignal,
    conversationId?: string,
  ): Promise<void>;
  getMCPServerStatus(): Promise<MCPServerStatusData>;
  getAvailableTools(): Promise<ToolsResponse>;
  getProviderStatus(): Promise<ProviderStatusData>;
  getConversations(): Promise<ConversationsResponse>;
  getConversationById(id: string): Promise<ConversationRecord>;
  deleteConversation(id: string): Promise<void>;
  toggleConversationStar(id: string): Promise<{ isStarred: boolean }>;
  renameConversation(id: string, title: string): Promise<{ title: string }>;
}

export class McpChat implements McpChatApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async sendChatMessage(
    messages: ChatMessage[],
    enabledTools: string[] = [],
    signal?: AbortSignal,
    conversationId?: string,
  ): Promise<ChatResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('mcp-chat');

    const response = await this.fetchApi.fetch(`${baseUrl}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages,
        enabledTools,
        conversationId,
      }),
      signal,
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async sendChatMessageStream(
    messages: ChatMessage[],
    enabledTools: string[] = [],
    onEvent: (event: StreamEvent) => void,
    signal?: AbortSignal,
    conversationId?: string,
  ): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('mcp-chat');

    const response = await this.fetchApi.fetch(`${baseUrl}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        messages,
        enabledTools,
        conversationId,
      }),
      signal,
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      let result = await reader.read();
      while (!result.done) {
        const value = result.value;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (trimmed.startsWith('data: ')) {
            try {
              const event: StreamEvent = JSON.parse(trimmed.slice(6));
              onEvent(event);
            } catch {
              // Skip malformed JSON
            }
          }
        }

        result = await reader.read();
      }
    } finally {
      reader.releaseLock();
    }
  }

  async getMCPServerStatus(): Promise<MCPServerStatusData> {
    const baseUrl = await this.discoveryApi.getBaseUrl('mcp-chat');
    const response = await this.fetchApi.fetch(`${baseUrl}/mcp/status`);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async getAvailableTools(): Promise<ToolsResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('mcp-chat');

    const response = await this.fetchApi.fetch(`${baseUrl}/tools`);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getProviderStatus(): Promise<ProviderStatusData> {
    const baseUrl = await this.discoveryApi.getBaseUrl('mcp-chat');

    const response = await this.fetchApi.fetch(`${baseUrl}/provider/status`);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getConversations(): Promise<ConversationsResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('mcp-chat');

    const response = await this.fetchApi.fetch(`${baseUrl}/conversations`);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getConversationById(id: string): Promise<ConversationRecord> {
    const baseUrl = await this.discoveryApi.getBaseUrl('mcp-chat');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/conversations/${id}`,
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async deleteConversation(id: string): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('mcp-chat');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/conversations/${id}`,
      { method: 'DELETE' },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  async toggleConversationStar(id: string): Promise<{ isStarred: boolean }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('mcp-chat');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/conversations/${id}/star`,
      { method: 'PATCH' },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async renameConversation(
    id: string,
    title: string,
  ): Promise<{ title: string }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('mcp-chat');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/conversations/${id}/title`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      },
    );

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }
}
